import { z } from 'zod';

export const NOTE_SUGGESTIONS_EVENT = 'note_suggestions';
export const NOTE_GENERATION_PHASE_EVENT = 'note_generation_phase';
export const PERSONALISATION_STREAM_CAPABILITY = 'personalisation_stream_v1';

export const NOTE_SUGGESTION_FEEDBACK_VALUES = [
  'accepted',
  'rejected',
  'skipped',
  // User edited the note before reacting to the card. We clear the card
  // automatically because the backend's char offsets are no longer valid
  // against the edited text; this signal tells the backend why.
  'skipped_edit',
] as const;

export type NoteSuggestionFeedback = (typeof NOTE_SUGGESTION_FEEDBACK_VALUES)[number];

const replacementApplicationSchema = z.object({
  original: z.string(),
  replacement: z.string(),
  start: z.number().int().nonnegative(),
  end: z.number().int().nonnegative(),
});

const strategyResultSchema = z.object({
  // Optional during rollout — absent when the FE talks to a backend that
  // predates the feedback endpoint. When present, it's the Mongo id we
  // echo back on the feedback POST.
  suggestion_id: z.string().optional(),
  strategy_name: z.string(),
  memory_type: z.string(),
  applications: z.array(replacementApplicationSchema),
  metadata: z.record(z.string(), z.unknown()).optional().default({}),
});

// `opt_in_ask` is the graduation preview mode: the note streams live and the
// frame proposes an edit inline, inviting the user to turn on auto-apply.
// Unknown/future modes degrade to non-mutating ASK rather than dropping the
// entire frame.
export const NOTE_SUGGESTION_MODES = ['ask', 'apply', 'opt_in_ask'] as const;
export type NoteSuggestionMode = (typeof NOTE_SUGGESTION_MODES)[number];

export const noteSuggestionsFrameSchema = z.object({
  version: z.number(),
  strategies: z.array(strategyResultSchema),
  mode: z.enum(NOTE_SUGGESTION_MODES).catch('ask'),
});

export const NOTE_GENERATION_PHASES = [
  'applying_personalisation',
  'personalisation_complete',
  'personalisation_skipped',
] as const;

export const noteGenerationPhaseFrameSchema = z.object({
  version: z.literal(1),
  generation_id: z.string().min(1),
  phase: z.enum(NOTE_GENERATION_PHASES),
});

export type ReplacementApplication = z.infer<typeof replacementApplicationSchema>;
export type StrategyResult = z.infer<typeof strategyResultSchema>;
export type NoteSuggestionsFrame = z.infer<typeof noteSuggestionsFrameSchema>;
export type NoteGenerationPhase = (typeof NOTE_GENERATION_PHASES)[number];
export type NoteGenerationPhaseFrame = z.infer<typeof noteGenerationPhaseFrameSchema>;

export type SuggestionEntry = {
  strategyName: string;
  memoryType: string;
  applicationIndex: number;
  application: ReplacementApplication;
};
